import React, { useEffect, useState } from 'react';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { Eyebrow, Badge } from '../components/common/Badge';
import { Icon } from '../lib/icons';
import { dataService } from '../services/dataService';
import { useLanguage } from '../context/LanguageContext';

function FilterPills({ filters, active, onChange }) {
  return (
    <div className="no-scrollbar -mx-6 flex gap-3 overflow-x-auto px-6 pb-2">
      {filters.map((f) => {
        const isActive = active === f.id;
        return (
          <button
            key={f.id}
            onClick={() => onChange(f.id)}
            className={`flex h-11 shrink-0 items-center gap-2 rounded-btn border px-4 font-sans text-sm transition-colors ${
              isActive
                ? 'border-paelbi-brown bg-paelbi-brown text-white'
                : 'border-paelbi-line bg-white text-paelbi-ink/80 hover:border-paelbi-brown/50'
            }`}
          >
            {f.icon && <Icon name={f.icon} size={18} strokeWidth={1.75} />}
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

function HeartButton() {
  const [on, setOn] = useState(false);
  return (
    <button
      onClick={() => setOn((v) => !v)}
      aria-label={on ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
      aria-pressed={on}
      className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-paelbi-brown shadow-soft transition-transform hover:scale-105"
    >
      <Icon name="heart" size={18} className={on ? 'fill-paelbi-brown' : ''} />
    </button>
  );
}

function ExperienceCard({ card }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-card border border-paelbi-line bg-white shadow-card transition-all hover:-translate-y-1 hover:shadow-soft">
      <div className="relative">
        <img src={card.image} alt={card.title} loading="lazy" className="h-48 w-full object-cover" />
        <HeartButton />
        <span className="absolute bottom-3 left-3"><Badge className="bg-white/90">{card.badge}</Badge></span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-xl text-paelbi-ink">{card.title}</h3>
        <p className="mt-1 font-sans text-sm italic text-paelbi-brown">{card.place}</p>
        <p className="mt-3 flex-1 font-sans text-sm leading-relaxed text-paelbi-ink/70">{card.text}</p>
        <div className="mt-5 flex items-center justify-between border-t border-paelbi-line pt-4">
          <div className="flex items-center gap-4 font-sans text-xs text-paelbi-ink/70">
            <span className="flex items-center gap-1.5"><Icon name="clock" size={15} />{card.duration}</span>
            <span className="flex items-center gap-1.5"><Icon name="users" size={15} />{card.people}</span>
          </div>
          <span className="font-sans text-sm font-bold text-paelbi-ink">{card.price}</span>
        </div>
      </div>
    </article>
  );
}

export function Esperienze() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState('all');
  const { t, lang } = useLanguage();
  useEffect(() => { dataService.getExperiences().then(setData); }, [lang]);
  if (!data) return null;

  const cards = active === 'all' ? data.cards : data.cards.filter((c) => c.cat === active);

  return (
    <div className="pb-20 pt-8">
      {/* HERO */}
      <Container>
        <section className="relative overflow-hidden rounded-card">
          <img src={data.hero.image} alt="Esperienze in Sardegna" className="h-[420px] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-lg px-8 md:px-12">
              <Eyebrow className="rounded-btn bg-white/80 px-3 py-1">{data.hero.eyebrow}</Eyebrow>
              <h1 className="mt-4 font-display text-4xl leading-[1.1] text-paelbi-ink md:text-[52px]">
                {data.hero.title.map((l, i) => <span key={`h-${i}`} className="block">{l}</span>)}
              </h1>
              <span className="mt-5 block h-px w-12 bg-paelbi-brown/60" />
              <p className="mt-5 max-w-md font-sans text-base text-paelbi-ink/80">{data.hero.text}</p>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <Button variant="primary" icon="arrowRight">{data.hero.primaryCta}</Button>
                <Button variant="secondary" iconLeft="play">{data.hero.secondaryCta}</Button>
              </div>
            </div>
          </div>
        </section>
      </Container>

      {/* FILTERS */}
      <Container className="mt-8">
        <FilterPills filters={data.filters} active={active} onChange={setActive} />
      </Container>

      {/* CARDS */}
      <Container className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl text-paelbi-ink md:text-[28px]">{t('Esperienze consigliate per te')}</h2>
          <div className="hidden items-center gap-2 rounded-btn border border-paelbi-line bg-white px-4 py-2 text-sm text-paelbi-ink/70 sm:flex">
            <span>{t('Ordina per')}</span>
            <span className="font-semibold text-paelbi-ink">{t('Consigliati')}</span>
            <Icon name="chevronRight" size={14} className="rotate-90" />
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {cards.map((c) => <ExperienceCard key={c.id} card={c} />)}
        </div>
      </Container>

      {/* SEASONAL */}
      <Container className="mt-16">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl text-paelbi-ink md:text-[28px]">Esperienze stagionali</h2>
          <Button variant="ghost" icon="arrowRight" className="h-11 px-5 text-sm">Vedi calendario</Button>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
          {data.seasonal.map((s) => (
            <article key={s.id} className="group overflow-hidden rounded-card border border-paelbi-line bg-white shadow-card transition-all hover:-translate-y-1">
              <img src={s.image} alt={s.title} loading="lazy" className="h-28 w-full object-cover" />
              <div className="p-4">
                <h3 className="font-display text-base text-paelbi-ink">{s.title}</h3>
                <p className="mt-1 font-sans text-xs text-paelbi-ink/60">{s.period}</p>
              </div>
            </article>
          ))}
        </div>
      </Container>

      {/* BENEFITS */}
      <Container className="mt-16">
        <div className="grid grid-cols-1 gap-8 rounded-card bg-paelbi-bg2/60 p-8 sm:grid-cols-2 lg:grid-cols-4">
          {data.benefits.map((b) => (
            <div key={b.title} className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white">
                <Icon name={b.icon} size={20} className="text-paelbi-brown" />
              </span>
              <div>
                <h3 className="font-display text-base text-paelbi-ink">{b.title}</h3>
                <p className="mt-1 font-sans text-sm text-paelbi-ink/70">{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
}

export default Esperienze;
